import { FullPageLoader } from '@/components/loaders';
import { OrgProvider } from '@/contexts/OrgContext';
import { SupportProvider } from '@/contexts/SupportContext';
import { BeaconTRPCProvider } from '@/lib/trpc-provider';
import { ToastProvider, TooltipProvider } from '@beacon/ui/components';
import React from 'react';
import { createBrowserRouter, Navigate, Outlet, RouterProvider } from 'react-router';
import { AuthTokenSync } from './components/auth/AuthTokenSync';
import { AdminRoute } from './components/auth/AdminRoute';
import { GlobalAdminRoute } from './components/auth/GlobalAdminRoute';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { NotFound } from './components/error/NotFound';
import { RouteErrorBoundary } from './components/error/RouteErrorBoundary';
import { ChromeExtensionAnnouncement } from './components/onboarding/ChromeExtensionAnnouncement';
import { FirstLoginOnboarding } from './components/onboarding/FirstLoginOnboarding';
import { Q3RoadmapSurvey } from './components/onboarding/Q3RoadmapSurvey';
import { Sidebar } from './components/sidebar/Sidebar';
import { TourProvider } from './tour/TourProvider';
import { SidebarProvider, useSidebar } from './components/sidebar/SidebarContext';
import { PromptsEmptyState } from './pages/admin/prompts/PromptsEmptyState';

const lazy = (factory: () => Promise<{ default: React.ComponentType }>) => {
  const Component = React.lazy(factory);

  return (
    <React.Suspense fallback={<FullPageLoader />}>
      <Component />
    </React.Suspense>
  );
};

function RootLayout() {
  const envLabel = import.meta.env.VITE_ENV_LABEL;
  const { collapsed } = useSidebar();

  return (
    <div
      className={[
        'bg-bg text-text-1 flex min-h-screen flex-col font-sans antialiased transition-[padding] duration-200',
        collapsed ? 'pl-[60px]' : 'pl-[220px]',
      ].join(' ')}
    >
      <AuthTokenSync />
      <BeaconTRPCProvider>
        <OrgProvider>
          {envLabel && (
            <div className="bg-primary-9 shrink-0 py-1 text-center text-xs font-semibold tracking-wide text-[#1A0A06] uppercase">
              {envLabel}
            </div>
          )}
          <TooltipProvider>
            <ToastProvider>
              <SupportProvider>
                <TourProvider>
                  <Sidebar />
                  <Outlet />
                  <FirstLoginOnboarding />
                  <ChromeExtensionAnnouncement />
                  <Q3RoadmapSurvey />
                </TourProvider>
              </SupportProvider>
            </ToastProvider>
          </TooltipProvider>
        </OrgProvider>
      </BeaconTRPCProvider>
    </div>
  );
}

function AdminLayout() {
  return (
    <div className="bg-bg text-text-1 min-h-screen font-sans antialiased">
      <AuthTokenSync />
      <BeaconTRPCProvider>
        <TooltipProvider>
          <ToastProvider>
            <GlobalAdminRoute />
          </ToastProvider>
        </TooltipProvider>
      </BeaconTRPCProvider>
    </div>
  );
}

function ContentLayout() {
  return (
    <div className="scroll grid-bg min-w-0 flex-1 overflow-auto px-[26px] py-[22px]">
      <Outlet />
    </div>
  );
}

export function createAppRouter() {
  return createBrowserRouter([
    {
      element: <Outlet />,
      errorElement: <RouteErrorBoundary />,
      children: [
        // Public routes
        {
          path: 'sign-in/*',
          element: lazy(() => import('@/pages/auth/SignInPage')),
        },
        {
          path: 'invite/accept',
          element: lazy(() => import('@/pages/invite/AcceptInvitePage')),
        },
        {
          path: 'extension/privacy-policy',
          element: lazy(() => import('@/pages/legal/ExtensionPrivacyPolicyPage')),
        },
        // Protected routes
        {
          element: <ProtectedRoute />,
          children: [
            {
              element: <AdminLayout />,
              children: [
                {
                  element: lazy(() => import('@/pages/admin/AdminShell')),
                  children: [
                    {
                      path: 'admin',
                      element: lazy(() => import('@/pages/admin/AdminPage')),
                    },
                    {
                      path: 'admin/insights',
                      element: lazy(() => import('@/pages/admin/InsightReviewPage')),
                    },
                    {
                      path: 'admin/insights/:insightId',
                      element: lazy(() => import('@/pages/admin/InsightReviewDetailPage')),
                    },
                    {
                      path: 'admin/usage',
                      element: lazy(() => import('@/pages/admin/AdminUsagePage')),
                    },
                    {
                      path: 'admin/prompts',
                      element: lazy(() => import('@/pages/admin/PromptConfigPage')),
                      children: [
                        { index: true, element: <PromptsEmptyState /> },
                        {
                          path: 'new/:category',
                          element: lazy(() => import('@/pages/admin/prompts/PromptVersionEditor')),
                        },
                        {
                          path: ':versionId',
                          element: lazy(() => import('@/pages/admin/prompts/PromptVersionEditor')),
                        },
                      ],
                    },
                  ],
                },
              ],
            },
            {
              element: <RootLayout />,
              children: [
                {
                  element: <Outlet />,
                  errorElement: <RouteErrorBoundary />, // errors with side bar
                  children: [
                    {
                      path: 'jobs/intake',
                      element: lazy(() => import('@/layouts/NewJobLayout')),
                      children: [
                        {
                          index: true,
                          element: lazy(() => import('@/pages/jobs/NewJobPage')),
                        },
                        {
                          path: 'chat',
                          element: lazy(() => import('@/pages/jobs/intake/IntakeChatPage')),
                        },
                      ],
                    },
                    {
                      element: <ContentLayout />,
                      children: [
                        {
                          index: true,
                          element: lazy(() => import('@/pages/DashboardPage')),
                        },
                        {
                          path: 'jobs',
                          element: <Navigate to="/" replace />,
                        },
                        {
                          path: 'jobs/:id',
                          element: lazy(() => import('@/pages/jobs/detail/JobDetailLayout')),
                          children: [
                            {
                              index: true,
                              element: lazy(() => import('@/pages/jobs/detail/JobOverviewPage')),
                            },
                            {
                              path: 'hiring-criteria',
                              element: lazy(() => import('@/pages/jobs/detail/HiringCriteriaPage')),
                            },
                            {
                              path: 'talent-pool',
                              element: lazy(() => import('@/pages/jobs/detail/TalentPoolLayout')),
                              children: [
                                {
                                  index: true,
                                  element: lazy(() => import('@/pages/jobs/detail/TalentPoolPage')),
                                },
                                {
                                  path: 'scoring-issues',
                                  element: lazy(() => import('@/pages/jobs/detail/ScoringIssuesPage')),
                                },
                                {
                                  element: lazy(() => import('@/pages/jobs/detail/AddCandidatesLayout')),
                                  children: [
                                    {
                                      path: 'manual',
                                      element: lazy(() => import('@/pages/jobs/detail/AddCandidatesManualPage')),
                                    },
                                    {
                                      path: 'import',
                                      element: lazy(() => import('@/pages/jobs/detail/AddCandidatesImportPage')),
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              path: 'pipeline',
                              element: lazy(() => import('@/pages/jobs/detail/PipelinePage')),
                            },
                            {
                              path: 'settings',
                              element: lazy(() => import('@/pages/jobs/detail/JobSettingsPage')),
                            },
                          ],
                        },
                        {
                          path: 'candidates',
                          element: lazy(() => import('@/pages/CandidatesPage')),
                        },
                        {
                          path: 'candidates/:displayId',
                          element: lazy(() => import('@/pages/CandidateDetailPage')),
                        },
                      ],
                    },
                    {
                      path: 'settings',
                      element: lazy(() => import('@/pages/settings/SettingsLayout')),
                      children: [
                        { index: true, element: <Navigate to="workspace" replace /> },
                        {
                          path: 'workspace',
                          element: lazy(() => import('@/pages/settings/WorkspacePage')),
                        },
                        {
                          element: <AdminRoute />,
                          children: [
                            {
                              path: 'queues',
                              element: lazy(() => import('@/pages/settings/QueuesPage')),
                            },
                          ],
                        },
                        {
                          path: 'team',
                          element: lazy(() => import('@/pages/settings/TeamPage')),
                        },
                      ],
                    },
                    {
                      // catch all
                      path: '*',
                      element: <NotFound />,
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
    },
  ]);
}

export function App({ router }: { router: ReturnType<typeof createAppRouter> }) {
  return (
    <SidebarProvider>
      <RouterProvider router={router} />
    </SidebarProvider>
  );
}
